import {
  Badge,
  Button,
  FlexBox,
  Icon,
  IconNames,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useCallback } from 'react';

import { createAppModal } from 'utils/modal';

import styles from './advancedInsightsTeaserModal.module.scss';

export const AnalyticsAdvancedInsightsTeaserModal = createAppModal(
  { type: 'analytics:advancedInsightsTeaser' },
  ({ isOpen, hide, meta }): JSX.Element => {
    const handleDismiss = useCallback(() => {
      hide();
      meta?.resolve({ isDismissed: true });
    }, [hide, meta]);

    const reasons = [
      'Uncover hidden trends in conversations',
      'Improve team efficiency with valuable data',
      'Personalize experiences through AI-driven analytics',
      'Measure and enhance customer satisfaction effortlessly',
    ];

    const features: [
      icon: IconNames,
      title: string,
      description: string,
      elevioArticleId: string,
    ][] = [
      [
        'star',
        'Custom Dashboards',
        'No more one-size-fits-all reporting. Build dashboards tailored to your business needs, tracking the exact KPIs that matter most to your support strategy.',
        '1442',
      ],
      [
        'ai',
        'AI Satisfaction Insights',
        'Go beyond surveys with AI-driven sentiment analysis. Instantly uncover customer satisfaction trends, top strengths, and key challenges—no extra effort needed.',
        '1704',
      ],
      [
        'magnifying-glass-chart',
        'Conversation Analysis',
        'Go beyond surface-level insights. Identify recurring pain points, sentiment trends, and the root causes of customer frustrations to proactively improve service experiences.',
        '1875',
      ],
      [
        'activity',
        'Activity Analysis',
        'Understand how your team operates with in-depth root cause analysis of agent activity. Spot inefficiencies, balance workloads, and optimize processes for maximum impact.',
        '1882',
      ],
    ];

    return (
      <Modal
        className={styles.modal}
        isOpen={isOpen}
        onClose={handleDismiss}
        allowEscClose={false}
        allowClickOutside={false}
      >
        <ModalHead
          title={
            <FlexBox gap="250" alignItems="center">
              <Badge variant="primary" size="medium">
                New
              </Badge>

              <Text.Heading variant="heading-2">Advanced Insights</Text.Heading>
            </FlexBox>
          }
          onClose={handleDismiss}
        />

        <ModalBody className={styles.modalBody}>
          <FlexBox
            flexDirection="column"
            padding="400"
            gap="300"
            background="purple.100"
            borderColor="purple.200"
            borderWidth="100"
            borderRadius="100"
          >
            <Text.Heading variant="heading-2" color="purple.500">
              Transform Your Customer Support with Data-Driven Decisions
            </Text.Heading>

            <Text.Paragraph variant="2">
              Welcome to the era of value-driven experiences—where every
              interaction counts. With Advanced Insights, you gain a deeper
              understanding of your customers' journey through valuable data and
              AI-powered analytics. Make informed decisions, optimize team
              performance, and elevate your service experience like never
              before.
            </Text.Paragraph>
          </FlexBox>

          <FlexBox
            flexDirection="column"
            padding="400"
            gap="400"
            background="neutral.000"
            borderColor="neutral.200"
            borderWidth="100"
            borderRadius="100"
          >
            <Text.Heading variant="heading-2">
              Why Advanced Insights?
            </Text.Heading>

            <Text.Paragraph variant="2">
              Customer service isn’t just about resolving customer
              inquiries—it’s about building lasting relationships.
              <br /> Advanced Insights gives you the power to move beyond
              guesswork, helping you:
            </Text.Paragraph>

            <div className={styles.reasons}>
              {reasons.map((reason, i) => (
                <FlexBox key={i} gap="200" alignItems="center">
                  <Icon name="check-circle" size={20} color="purple.500" />
                  <Text.Paragraph variant="1">{reason}</Text.Paragraph>
                </FlexBox>
              ))}
            </div>
          </FlexBox>

          <FlexBox
            flexDirection="column"
            padding="400"
            gap="400"
            background="neutral.000"
            borderColor="neutral.200"
            borderWidth="100"
            borderRadius="100"
          >
            <Text.Heading variant="heading-2">
              What is Advanced Insights?
            </Text.Heading>

            <div className={styles.features}>
              {features.map(
                ([icon, title, description, elevioArticleId], i) => (
                  <div key={i}>
                    <FlexBox>
                      <Icon name={icon} size={24} color="purple.500" />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="250">
                      <Text.Paragraph variant="1">{title}</Text.Paragraph>
                      <Text.Paragraph variant="2">{description}</Text.Paragraph>

                      <div>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            elevioClient.openArticle(elevioArticleId)
                          }
                        >
                          Learn more
                        </Button>
                      </div>
                    </FlexBox>
                  </div>
                ),
              )}
            </div>
          </FlexBox>
        </ModalBody>

        <ModalFoot confirmName="Dismiss" confirmOnClick={handleDismiss} />
      </Modal>
    );
  },
);
